import React, { Component } from 'react';
import { AgCharts } from 'ag-charts-community';
import moment from 'moment-timezone';
import './Dashboard.css';

const CHART_HEIGHT = 120;

// Account timezone is stored as "(UTC - 240) America/New_York".
// When it is missing or not a real zone, the popup falls back to UTC.
const resolveAccountTimeZone = () => {
  let stored = '';
  try {
    stored = sessionStorage.getItem('timeZone');
  } catch (e) {
    stored = '';
  }
  if ([undefined, null, '', 'null', 'undefined'].includes(stored)) {
    return 'UTC';
  }
  const value = String(stored).trim();
  if (moment.tz.zone(value)) {
    return value;
  }
  const ianaMatch = value.match(/[A-Za-z]+(?:\/[A-Za-z0-9_+-]+){1,2}/);
  if (ianaMatch && moment.tz.zone(ianaMatch[0])) {
    return ianaMatch[0];
  }
  const lastToken = value.substring(value.lastIndexOf(' ') + 1);
  if (lastToken && moment.tz.zone(lastToken)) {
    return lastToken;
  }
  return 'UTC';
};

const formatPopupTime = (value) => {
  const zone = resolveAccountTimeZone();
  const lang = localStorage.getItem('i18nextLng');
  const pattern = lang === 'en' ? 'MM/DD/YYYY hh:mm:ss A' : 'DD/MM/YYYY hh:mm:ss A';
  const when = moment(value);
  if (!when.isValid()) {
    return '';
  }
  return `${when.tz(zone).format(pattern)} (${zone})`;
};

const MIN_DRAG_PX = 6;

const toNumber = (value) => {
  if (value instanceof Date) {
    return value.getTime();
  }
  return Number(value);
};

export default class google extends Component {
  constructor(props) {
    super(props);
    this.chartHostRef = React.createRef();
    this.canvasRef = React.createRef();
    this.selectionRef = React.createRef();
    this.chart = null;
    this.overlayBound = false;
    this.chartSignature = '';
    this.drag = null;
    this.state = {
      selection: null,
      zoom: null,
    };
  }

  componentDidMount() {
    this.createOrUpdate();
    this.bindOverlay();
  }

  componentDidUpdate() {
    this.createOrUpdate();
  }

  componentWillUnmount() {
    this.unbindOverlay();
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
  }

  bindOverlay = () => {
    const canvas = this.canvasRef.current;
    if (!canvas || this.overlayBound) {
      return;
    }
    this.overlayBound = true;
    this.handleOverlayPointerDown = (event) => this.onOverlayPointerDown(event);
    this.handleOverlayPointerMove = (event) => this.onOverlayPointerMove(event);
    this.handleOverlayPointerUp = (event) => this.onOverlayPointerUp(event);
    this.stopGridMouseDown = (event) => {
      event.stopPropagation();
    };
    canvas.addEventListener('pointerdown', this.handleOverlayPointerDown);
    canvas.addEventListener('pointermove', this.handleOverlayPointerMove);
    canvas.addEventListener('pointerup', this.handleOverlayPointerUp);
    canvas.addEventListener('pointercancel', this.handleOverlayPointerUp);
    canvas.addEventListener('dblclick', this.onDoubleClick);
    canvas.addEventListener('mousedown', this.stopGridMouseDown);
  }

  unbindOverlay = () => {
    const canvas = this.canvasRef.current;
    if (!canvas || !this.overlayBound) {
      return;
    }
    canvas.removeEventListener('pointerdown', this.handleOverlayPointerDown);
    canvas.removeEventListener('pointermove', this.handleOverlayPointerMove);
    canvas.removeEventListener('pointerup', this.handleOverlayPointerUp);
    canvas.removeEventListener('pointercancel', this.handleOverlayPointerUp);
    canvas.removeEventListener('dblclick', this.onDoubleClick);
    canvas.removeEventListener('mousedown', this.stopGridMouseDown);
    this.overlayBound = false;
  }

  getChart = () => {
    if (!this.chart) {
      return null;
    }
    return this.chart.chart || this.chart;
  }

  convertFtoC = (tempF) => {
    if (![undefined, 'undefined', null, 'null', ''].includes(tempF) && this.props.tempUnit === 'C') {
      return parseFloat(((tempF - 32) * 5 / 9).toFixed(2));
    }
    return tempF;
  }

  getTemperatureUnit = () => {
    return this.props.tempUnit === 'C' ? '°C' : '°F';
  }

  dataFormatter = (data) => {
    const rows = [];
    for (let i = 0; i < data.length; i++) {
      const parsed = moment(data[i].date);
      if (!parsed.isValid()) {
        continue;
      }
      rows.push({
        date: parsed.toDate(),
        temperature: this.convertFtoC(parseInt((data[i].temperature), 0)),
      });
    }
    return rows;
  }

  getOptions = () => {
    const seriesName = `Temperature (${this.getTemperatureUnit()})`;
    const rows = this.dataFormatter(this.props.data || []);
    const zoom = this.state.zoom;
    const visibleRows = zoom
      ? rows.filter((row) => {
        const time = row.date.getTime();
        return time >= zoom.xMin && time <= zoom.xMax
          && row.temperature >= zoom.yMin && row.temperature <= zoom.yMax;
      })
      : rows;
    const plottedRows = visibleRows.length ? visibleRows : rows;
    const timeAxis = {
      type: 'time',
      position: 'bottom',
      label: { enabled: false },
      tick: { enabled: false },
      line: { enabled: false },
      gridLine: { enabled: false },
    };
    const numberAxis = {
      type: 'number',
      position: 'left',
      nice: !zoom,
      title: {
        text: seriesName,
        fontSize: 11,
      },
      label: { fontSize: 10 },
    };
    if (zoom) {
      timeAxis.min = new Date(zoom.xMin);
      timeAxis.max = new Date(zoom.xMax);
      numberAxis.min = zoom.yMin;
      numberAxis.max = zoom.yMax;
    }
    return {
      container: this.chartHostRef.current,
      data: plottedRows,
      height: CHART_HEIGHT,
      padding: { top: 8, right: 12, bottom: 4, left: 4 },
      legend: { enabled: false },
      tooltip: { wrapping: 'never' },
      series: [{
        type: 'line',
        xKey: 'date',
        yKey: 'temperature',
        yName: seriesName,
        stroke: '#7cb5ec',
        strokeWidth: 1.5,
        marker: {
          enabled: !!zoom && plottedRows.length > 0 && plottedRows.length <= 120,
          size: 5,
        },
        tooltip: {
          renderer: ({ datum, yKey, yName }) => ({
            title: formatPopupTime(datum.date),
            content: `${yName}: ${datum[yKey]}`,
          }),
        },
      }],
      axes: [timeAxis, numberAxis],
    };
  }

  getSignature = () => {
    const rows = this.dataFormatter(this.props.data || []);
    const zoom = this.state.zoom;
    const first = rows.length ? rows[0].date.getTime() : '';
    const last = rows.length ? rows[rows.length - 1].date.getTime() : '';
    const zoomKey = zoom ? [zoom.xMin, zoom.xMax, zoom.yMin, zoom.yMax].join(',') : 'full';
    return [rows.length, first, last, this.getTemperatureUnit(), zoomKey].join('|');
  }

  createOrUpdate = () => {
    if (!this.chartHostRef.current) {
      return;
    }
    const signature = this.getSignature();
    if (this.chart && signature === this.chartSignature) {
      return;
    }
    this.chartSignature = signature;
    const options = this.getOptions();
    try {
      if (this.chart) {
        this.chart.destroy();
        this.chart = null;
      }
      this.chart = AgCharts.create(options);
    } catch (err) {
      console.error('[TemperatureChart]', err);
    }
  }

  eventToElement = (event, element) => {
    const bounds = element.getBoundingClientRect();
    return {
      x: event.clientX - bounds.left,
      y: event.clientY - bounds.top,
      width: bounds.width,
      height: bounds.height,
    };
  }

  getSeriesRect = () => {
    const chart = this.getChart();
    const rect = chart && chart.seriesRect;
    if (!rect || !rect.width || !rect.height) {
      return null;
    }
    return rect;
  }

  hideSelection = () => {
    const selection = this.selectionRef.current;
    if (!selection) {
      return;
    }
    selection.style.display = 'none';
  }

  paintSelection = (x1, y1, x2, y2) => {
    const selection = this.selectionRef.current;
    if (!selection) {
      return;
    }
    selection.style.display = 'block';
    selection.style.left = Math.min(x1, x2) + 'px';
    selection.style.top = Math.min(y1, y2) + 'px';
    selection.style.width = Math.abs(x2 - x1) + 'px';
    selection.style.height = Math.abs(y2 - y1) + 'px';
  }

  onOverlayPointerDown = (event) => {
    if (event.button != null && event.button !== 0) {
      return;
    }
    if (event.target && event.target.closest && event.target.closest('.temp-chart-reset')) {
      return;
    }
    const canvas = this.canvasRef.current;
    if (!canvas) {
      return;
    }
    const point = this.eventToElement(event, canvas);
    this.drag = {
      pointerId: event.pointerId,
      startX: point.x,
      startY: point.y,
      startClientX: event.clientX,
      startClientY: event.clientY,
    };
    if (canvas.setPointerCapture) {
      canvas.setPointerCapture(event.pointerId);
    }
    event.stopPropagation();
  }

  onOverlayPointerMove = (event) => {
    if (!this.drag || this.drag.pointerId !== event.pointerId) {
      return;
    }
    const canvas = this.canvasRef.current;
    if (!canvas) {
      return;
    }
    const point = this.eventToElement(event, canvas);
    this.paintSelection(this.drag.startX, this.drag.startY, point.x, point.y);
    event.preventDefault();
    event.stopPropagation();
  }

  onOverlayPointerUp = (event) => {
    if (!this.drag || (event.pointerId != null && this.drag.pointerId !== event.pointerId)) {
      return;
    }
    const drag = this.drag;
    this.drag = null;
    this.hideSelection();
    const canvas = this.canvasRef.current;
    const point = canvas ? this.eventToElement(event, canvas) : { x: drag.startX, y: drag.startY };
    event.preventDefault();
    event.stopPropagation();
    if (Math.abs(point.x - drag.startX) < MIN_DRAG_PX || Math.abs(point.y - drag.startY) < MIN_DRAG_PX) {
      return;
    }
    this.zoomToSelection(drag.startClientX, drag.startClientY, event.clientX, event.clientY);
  }

  zoomToSelection = (clientX1, clientY1, clientX2, clientY2) => {
    const chart = this.getChart();
    const chartElement = chart && chart.element;
    const rect = this.getSeriesRect();
    const axes = chart && chart.axes;
    const xAxis = axes && axes.filter((axis) => axis.position === 'bottom')[0];
    const yAxis = axes && axes.filter((axis) => axis.position === 'left')[0];
    const rows = this.dataFormatter(this.props.data || []);
    if (!chartElement || !rows.length) {
      return;
    }
    const bounds = chartElement.getBoundingClientRect();
    const x1 = clientX1 - bounds.left;
    const y1 = clientY1 - bounds.top;
    const x2 = clientX2 - bounds.left;
    const y2 = clientY2 - bounds.top;
    let xStart;
    let xEnd;
    let yStart;
    let yEnd;
    if (rect && xAxis && yAxis && xAxis.scale && yAxis.scale && xAxis.scale.invert && yAxis.scale.invert) {
      const left = Math.max(rect.x, Math.min(x1, x2));
      const right = Math.min(rect.x + rect.width, Math.max(x1, x2));
      const top = Math.max(rect.y, Math.min(y1, y2));
      const bottom = Math.min(rect.y + rect.height, Math.max(y1, y2));
      xStart = toNumber(xAxis.scale.invert(Math.max(0, left - rect.x)));
      xEnd = toNumber(xAxis.scale.invert(Math.max(0, right - rect.x)));
      yStart = toNumber(yAxis.scale.invert(Math.max(0, top - rect.y)));
      yEnd = toNumber(yAxis.scale.invert(Math.max(0, bottom - rect.y)));
    }
    if (![xStart, xEnd, yStart, yEnd].every((value) => Number.isFinite(value))) {
      const width = bounds.width || 1;
      const height = bounds.height || 1;
      const xRatioA = Math.min(1, Math.max(0, Math.min(x1, x2) / width));
      const xRatioB = Math.min(1, Math.max(0, Math.max(x1, x2) / width));
      const yRatioA = Math.min(1, Math.max(0, Math.min(y1, y2) / height));
      const yRatioB = Math.min(1, Math.max(0, Math.max(y1, y2) / height));
      const zoom = this.state.zoom;
      const start = zoom ? zoom.xMin : rows[0].date.getTime();
      const end = zoom ? zoom.xMax : rows[rows.length - 1].date.getTime();
      let tempMin = zoom ? zoom.yMin : rows[0].temperature;
      let tempMax = zoom ? zoom.yMax : rows[0].temperature;
      if (!zoom) {
        for (let i = 1; i < rows.length; i++) {
          tempMin = Math.min(tempMin, rows[i].temperature);
          tempMax = Math.max(tempMax, rows[i].temperature);
        }
      }
      xStart = start + xRatioA * (end - start);
      xEnd = start + xRatioB * (end - start);
      yStart = tempMax - yRatioA * (tempMax - tempMin);
      yEnd = tempMax - yRatioB * (tempMax - tempMin);
    }
    if (![xStart, xEnd, yStart, yEnd].every((value) => Number.isFinite(value))) {
      return;
    }
    let yMin = Math.min(yStart, yEnd);
    let yMax = Math.max(yStart, yEnd);
    if (yMax === yMin) {
      yMin -= 0.5;
      yMax += 0.5;
    }
    const xMin = Math.min(xStart, xEnd);
    const xMax = Math.max(xStart, xEnd);
    if (!(xMax > xMin)) {
      return;
    }
    this.setState({
      zoom: { xMin, xMax, yMin, yMax },
    });
  }

  resetZoom = (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (!this.state.zoom) {
      return;
    }
    this.setState({ zoom: null, selection: null });
  }

  onDoubleClick = (event) => {
    if (!this.state.zoom) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.resetZoom();
  }

  render() {
    const { zoom } = this.state;
    const startLabel = zoom
      ? this.props.convertTimeZone(zoom.xMin)
      : this.props.convertTimeZone(this.props.startDate);
    const endLabel = zoom
      ? this.props.convertTimeZone(zoom.xMax)
      : this.props.convertTimeZone(this.props.endDate);
    return (
      <div className="temp-chart">
        <div className="temp-chart-canvas" ref={this.canvasRef}>
          <div ref={this.chartHostRef} className="temp-chart-plot" />
          <div ref={this.selectionRef} className="temp-chart-selection" />
          {zoom ? (
            <button type="button" className="temp-chart-reset" onClick={this.resetZoom}>
              Reset zoom
            </button>
          ) : null}
        </div>
        <div className="temp-chart-dates font-style">
          <span>{startLabel}</span>
          <span>{endLabel}</span>
        </div>
      </div>
    )
  }
}



// WEBPACK FOOTER //
// ./src/components/reports/temparature_monitoring/Dashboard.js